import { useMutation, useQuery } from "@apollo/client";
import {
  GetDeliverableReportDocument,
  SetReceivedBackOrdersSeenMutation,
} from "../order/graphql/order-report-graphql";
import { useReceivedBackOrderCount } from "../inventory/component/ReceivedBackOrderMenuItem";
import { useEffect, useState } from "react";
import { InputCard } from "../inventory/component/InputCard";
import { Table, Button, Modal } from "antd";
import { translate } from "../../core/translate";
import { purchaseOrderBackOrderDeliverables } from "../order/type/order-report";
import { CheckOutlined, MailOutlined } from "@ant-design/icons";
import {
  SendEmailTo,
  SetEmailSent,
} from "../order/graphql/order-queries.graphql";
import { message } from "antd";
import { SendEmailWithReceiver } from "../../core/component/SendEmailWithReceiver";

const DeliverableReport = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [openSendEmail, setOpenSendEmail] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] =
    useState<purchaseOrderBackOrderDeliverables>();
  const { data, loading, refetch } = useQuery(GetDeliverableReportDocument, {
    variables: {
      request: {
        take: pageSize,
        skip: currentPage * pageSize,
      },
    },
  });

  const {refetch: refetchNewCount} = useReceivedBackOrderCount();
  const [setReceivedBackOrdersSeen] = useMutation(SetReceivedBackOrdersSeenMutation);

  useEffect(() => {
    setReceivedBackOrdersSeen()
      .then(refetchNewCount)
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [setEmailSent] = useMutation(SetEmailSent);

  const [sendEmailTo, { loading: sendingEmailTo }] = useMutation(
    SendEmailTo,
    {
      onCompleted: () => {
        setEmailSent({
          variables: {
            id: selectedRecord?.id ?? 0,
          },
        }).then(() => {
          refetch();
        });
        setOpenSendEmail(false);
        messageApi.success(translate("email", "sent"));
      },
      onError: (error) => {
        messageApi.error(error.message);
      },
    }
  );

  return (
    <>
      <InputCard contained>
        {contextHolder}
        <Table<purchaseOrderBackOrderDeliverables>
          loading={loading}
          columns={[
            {
              dataIndex: "orderId",
              title: translate("order", "number_abbr"),
              width: 100,
            },
            {
              dataIndex: "contactId",
              title: translate("contact", "ID"),
              width: 150,
            },
            {
              dataIndex: "name",
              title: translate("name"),
              width: 150,
            },
            {
              dataIndex: "sku",
              title: translate("sku"),
              width: 150,
            },
            {
              dataIndex: "description",
              title: translate("description"),
              width: 300,
            },
            {
              dataIndex: "purchaseOrderNumber",
              title: translate("purchase_order_nu_abbr"),
              width: 150,
            },
            {
              dataIndex: "email",
              title: translate("email"),
              width: 150,
            },
            {
              dataIndex: "phone",
              title: translate("phone"),
              width: 150,
            },
            {
              dataIndex: "id",
              title: translate("actions"),
              width: 100,
              render: (id, record) => (
                <>
                  <Button
                    icon={<MailOutlined />}
                    disabled={record.emailSent}
                    loading={sendingEmailTo}
                    type="text"
                    shape="circle"
                    onClick={() => {
                      setSelectedRecord(record);
                      setOpenSendEmail(true);
                    }}
                  />
                  {record.emailSent && (
                    <Button
                      icon={<CheckOutlined style={{ color: "green" }} />}
                      type="text"
                      shape="circle"
                      disabled
                    />
                  )}
                </>
              ),
            },
          ]}
          dataSource={data?.purchaseOrderBackOrderDeliverablesFind.data ?? []}
          pagination={{
            total: data?.purchaseOrderBackOrderDeliverablesFind.count,
            position: ["bottomCenter"],
            pageSize: pageSize,
            showSizeChanger: true,
          }}
          onChange={(e) => {
            if (e.current) setCurrentPage(e.current - 1);

            if (e.pageSize) setPageSize(e.pageSize);
          }}
        />
      </InputCard>
      <SendEmailWithReceiver
        attachment={{
          enabled: true,
          prefix: "order-attachments",
        }}
        open={openSendEmail}
        onCancel={() => setOpenSendEmail(false)}
        onSend={(data) =>
          sendEmailTo({
            variables: {
              input: {
                to: data.to,
                subject: data.subject,
                text: data.text,
              },
            },
          })
        }
        sending={sendingEmailTo}
        data={{
          to: selectedRecord?.email ?? "",
          subject: `Update on Order ${selectedRecord?.orderId} | Joseph Gann Jewelers`,
          text: `Dear ${selectedRecord?.name},
Good news! Your back order for ${selectedRecord?.sku} - ${selectedRecord?.description} is now available for pickup.

**Next steps:**
1. Visit us at 387 Washington Street, 4th Floor in Boston during 10:00AM to 3:00PM.
2. Bring your order ID: ${selectedRecord?.orderId}.
3. Questions? Reply to this email or call +16174264932

Thank you for your patience!

Best regards,
Joseph Gann Jewelers`,
        }}
      />
    </>
  );
};

export default DeliverableReport;
